import { I18n } from 'i18n-js'
import { capitalize } from '../helpers'
import { appsLanguagePositionSelected } from '../helpers/app'
import StorageService from './storage'
import ca from '../translations/ca.json'
import de from '../translations/de.json'
import el from '../translations/el.json'
import en from '../translations/en.json'
import es from '../translations/es.json'
import eu from '../translations/eu.json'
import fr from '../translations/fr.json'
import hr from '../translations/hr.json'
import hu from '../translations/hu.json'
import it from '../translations/it.json'
import nl from '../translations/nl.json'
import pt from '../translations/pt.json'
import si from '../translations/si.json'

const i18n = new I18n({
  ca,
  de,
  el,
  en,
  es,
  eu,
  fr,
  hr,
  hu,
  it,
  nl,
  pt,
  si
})
i18n.enableFallback = true

export type Language = 'ca' | 'de' | 'el' | 'en' | 'es' | 'eu' | 'fr' | 'hr' | 'hu' | 'it' | 'nl' | 'pt' | 'si'

class LiteralsService {
  private language = this.getInitialLanguage()

  constructor() {
    this.updateDocumentLanguage()
  }

  public getLanguageCode(language: string): string {
    return language === 'si' ? 'sl' : language
  }

  private updateDocumentLanguage(): void {
    document.documentElement.lang = this.getLanguageCode(this.language)
  }

  public get(key: string, capitalized = false): string {
    const res = this.getKey(key)
    return capitalized ? capitalize(res) : res
  }

  public getOrDefault(key: string | undefined | null, fallbackKey: string, capitalized: boolean = false): string {
    if (key && i18n.translations[this.language] && (i18n.translations[this.language] as any)[key]) {
      return this.get(key, capitalized)
    }
    if (key && i18n.translations[this.getDefaultLanguageForTranslations()] && (i18n.translations[this.getDefaultLanguageForTranslations()] as any)[key]) {
      return this.get(key, capitalized)
    }
    return this.get(fallbackKey, capitalized)
  }

  // A language picker names every option in its own language, not in the one
  // the interface happens to be using.
  public getInLanguage(key: string, language: string, capitalized = false): string {
    const res = i18n.t(key, { locale: language }) || key
    return capitalized ? capitalize(res) : res
  }

  public isLanguageActive(language: string): boolean {
    return this.language === language
  }

  public setLanguage(language: string): void {
    this.language = language
    this.updateDocumentLanguage()
  }

  public getLanguage(): string {
    return this.language
  }

  // The language to start with on any page load: the one the user picked last, if the brand still
  // offers it (a saved one may come from another build on the same origin), or the default otherwise.
  public getInitialLanguage(): string {
    const picked = StorageService.getLanguage()
    return picked && this.isLanguageAvailable(picked) ? picked : this.getDefaultLanguageForTranslations()
  }

  // The browser's language when the brand offers it; otherwise the first of the brand's languages.
  public getDefaultLanguageForTranslations(): string {
    const brandLanguages = this.getAvailableLanguages()
    const browserLanguage = navigator.language.split('-')[0]
    if (brandLanguages.includes(browserLanguage)) return browserLanguage
    return brandLanguages[0] ?? 'en'
  }

  public isLanguageAvailable(language: string): boolean {
    return this.getAvailableLanguages().includes(language)
  }

  private getAvailableLanguages(): string[] {
    const translated = Object.keys(i18n.translations)
    const brandLanguages = appsLanguagePositionSelected?.languages ?? translated
    return brandLanguages.filter(language => translated.includes(language))
  }

  private getKey(key: string): string {
    return i18n.t(key, {locale: this.language}) || key
  }
}

export default new LiteralsService()
